import { useState } from 'react';
import { ResumenCobro, SeccionPacto, SeccionCuotasYPagos, SeccionBitacora } from './SeccionesCobro';
import ModalRegistrarPago from './ModalRegistrarPago';
import ModalPagarCuota from './ModalPagarCuota';
import ModalPlanCuotas from './ModalPlanCuotas';
import { montoAFinanciar } from './planCuotas';
import { usePermisosCobro } from './permisosCobro';

/**
 * El detalle de un cobro: un Resumen (cuánto se pactó, cuánto se cobró, cuánto falta, cómo viene el
 * plan) y debajo las secciones — Información del pacto, Plan de cuotas y pagos, y Bitácora — con los
 * modales que abren (pagar una cuota, registrar otro pago, crear o reprogramar el plan).
 *
 * Es lo ÚNICO que dibuja un cobro. Lo usan la ficha del trámite (SeccionCobranzaTramite), el modal
 * del cliente y de los listados (ModalDetalleCobro) y la atención de un turno de cobro
 * (VistaDetalladaAtencionCobro): por eso las tres se ven y se comportan igual, y un cambio acá llega
 * a todas. Antes cada una armaba lo suyo (una con secciones, dos con pestañas) y se iban separando.
 *
 * @param cobro         el detalle del cobro (CobroDetalleDto)
 * @param onCambio      vuelve a pedir el cobro; se llama después de guardar cualquier cosa
 * @param mostrarPacto  false oculta "Información del pacto". La atención de un turno lo apaga a
 *                      propósito: editar el pacto, dar el alta o dar de baja no se decide en el
 *                      mostrador mientras el cliente espera (ver VistaDetalladaAtencionCobro)
 */
export default function DetalleCobranza({ cobro, onCambio, mostrarPacto = true }) {
    const [modalPagoAbierto, setModalPagoAbierto] = useState(false);
    const [modalPlanAbierto, setModalPlanAbierto] = useState(false);
    // La cuota se guarda aparte de si el modal está abierto: al cerrarse, el modal necesita seguir
    // teniendo la cuota mientras dura la animación de salida.
    const [cuotaAPagar, setCuotaAPagar] = useState(null);
    const [modalPagarCuotaAbierto, setModalPagarCuotaAbierto] = useState(false);

    const cuotasVigentes = (cobro.cuotas || []).filter((c) => !c.anulada);
    // Pagos, cuotas y bitácora: cualquiera. La configuración y la baja: ver permisosCobro.js.
    const { puedeConfigurar, puedeDarDeBaja } = usePermisosCobro(cobro);

    return (
        <>
            <ResumenCobro cobro={cobro} />

            <div className="mt-4">
                {mostrarPacto && (
                    <SeccionPacto cobro={cobro} onCambio={onCambio}
                        puedeConfigurar={puedeConfigurar} puedeDarDeBaja={puedeDarDeBaja} />
                )}
                <SeccionCuotasYPagos
                    cobro={cobro}
                    onAbrirModalPlan={() => setModalPlanAbierto(true)}
                    onAbrirModalPago={() => setModalPagoAbierto(true)}
                    onPagarCuota={(cuota) => { setCuotaAPagar(cuota); setModalPagarCuotaAbierto(true); }}
                    onCambio={onCambio}
                />
                <SeccionBitacora cobro={cobro} onCambio={onCambio} />
            </div>

            <ModalRegistrarPago
                open={modalPagoAbierto}
                idCobro={cobro.idCobro}
                cuotas={cuotasVigentes.filter((c) => !c.pagada)}
                onClose={() => setModalPagoAbierto(false)}
                onGuardado={onCambio}
            />
            {cuotaAPagar && (
                <ModalPagarCuota
                    open={modalPagarCuotaAbierto}
                    idCobro={cobro.idCobro}
                    cuota={cuotaAPagar}
                    cantidadCuotas={cuotasVigentes.length}
                    pagos={cobro.pagos || []}
                    onClose={() => setModalPagarCuotaAbierto(false)}
                    onGuardado={onCambio}
                />
            )}
            <ModalPlanCuotas
                open={modalPlanAbierto}
                idCobro={cobro.idCobro}
                cuotasVigentes={cuotasVigentes}
                montoBase={montoAFinanciar(cobro)}
                onClose={() => setModalPlanAbierto(false)}
                onGuardado={onCambio}
            />
        </>
    );
}
